import React, { useState, Suspense, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  Loader2, 
  Truck, 
  MapPin, 
  Navigation, 
  BarChart3, 
  History, 
  Hospital, 
  Building2, 
  Mail, 
  Car, 
  Settings, 
  User, 
  Zap 
} from 'lucide-react'
import BookingTrackingCard from '../components/ambulance/BookingTrackingCard'
import Sidebar from '../components/Sidebar'
import { UniversalTopBar } from '../components/Topbar'
import MaintenanceBanner from '../components/commandcenter/MaintenanceBanner'
import FloatingNavBar from '../components/Navbar'
import ProfilePage from './ProfilePage'
import ProfileScreen from './ProfileScreen'
import VehiclesPage from './VehiclesPage'
import VehicleHistoryPage from './VehicleHistoryPage'
import NotificationContainer from '../components/ui/NotificationContainer'
import DateSelector from '../components/ui/DateSelector'
import DateRangeSelector from '../components/ui/DateRangeSelector'
import Pagination from '../components/ui/Pagination'
import TotalPageNumber from '../components/ui/TotalPageNumber'
import useNotifications from '../hooks/useNotifications'
import { useAuth } from '../contexts/AuthContext'
import { useWebSocket } from '../hooks/useWebSocket'
import hospitalService from '../services/hospitalService'
import DashboardIcon from '../svg/Dashboard icon.svg'
import AnalyticsIcon from '../svg/Analytics icon.svg'
import SettingIcon from '../svg/Setting icon.svg'
import ProfileIcon from '../svg/Profile icon.svg'
import VehiclesIcon from '../svg/Vehicles icon.svg'
import OperationsIcon from '../svg/Operations icon.svg'
import RevenueIcon from '../svg/Revenue icon.svg'
import HospitalsIcon from '../svg/Hospitals icon.svg'
import HospitalPartnershipRequests from './HospitalPartnershipRequests'
import OperatorIcon from '../svg/Operators icon.svg'
import DriverIcon from '../svg/Drivers icon.svg'
import VehicleIcon from '../svg/Vehicles icon.svg'
import UserIcon from '../svg/User icon.svg'

// Lazy load components for better performance
const HospitalOverviewCards = React.lazy(() => import('../components/hospital/HospitalOverviewCards'))
const HospitalAnalytics = React.lazy(() => import('../components/hospital/HospitalAnalytics'))
const HospitalTrackingScreen = React.lazy(() => import('../components/hospital/HospitalTrackingScreen'))
const PaymentReports = React.lazy(() => import('../components/hospital/PaymentReports'))
const Settlements = React.lazy(() => import('../components/hospital/Settlements'))
const HospitalBooking = React.lazy(() => import('../components/hospital/HospitalBooking'))
const PartnerFleetManagement = React.lazy(() => import('../components/hospital/PartnerFleetManagement'))
const Partnerships = React.lazy(() => import('../components/hospital/Partnerships'))
const AmbulanceBookingTabs = React.lazy(() => import('../components/ambulance/AmbulanceBookingTabs'))
const BookAmbulanceForm = React.lazy(() => import('../components/ambulance/BookAmbulanceForm'))
const AmbulanceInstantBooking = React.lazy(() => import('./AmbulanceInstantBooking'))
const AmbulanceInstantBookingDuplicate = React.lazy(() => import('./AmbulanceInstantBookingDuplicate'))
const TrackBookings = React.lazy(() => import('../components/ambulance/TrackBookings'))
const BookingHistory = React.lazy(() => import('../components/ambulance/BookingHistory'))
const RequestsTable = React.lazy(() => import('../components/requests/RequestsTable'))
const Modal = React.lazy(() => import('../components/requests/Modal'))
const CreateRequestForm = React.lazy(() => import('../components/requests/CreateRequestForm'))
const LiveMap = React.lazy(() => import('../components/tracking/LiveMap'))
const Filters = React.lazy(() => import('../components/tracking/Filters'))
const SAdminDashboard = React.lazy(() => import('../components/dashboard/SAdminDashboard'))
const OverviewScreen = React.lazy(() => import('../components/dashboard/OverviewScreen'))
const RevenueScreen = React.lazy(() => import('../components/dashboard/RevenueScreen'))
const HistoryScreen = React.lazy(() => import('../components/history/HistoryScreen'))
const OperatorScreen = React.lazy(() => import('../components/operators/OperatorScreen'))
const DriverScreen = React.lazy(() => import('../components/drivers/DriverScreen'))
const VehicleScreen = React.lazy(() => import('../components/vehicles/VehicleScreen'))
const SettingsScreen = React.lazy(() => import('../components/settings/SettingsScreen'))

const HospitalDashboard = ({ dashboardType = 'hospital' }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedAmbulanceType, setSelectedAmbulanceType] = useState('bls')
  const [requests, setRequests] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSidebarItem, setSelectedSidebarItem] = useState('book_ambulance')
  // Initialize WebSocket connection for real-time updates
  const { isConnected, connectionStatus } = useWebSocket()
  const [loadedTabs, setLoadedTabs] = useState(new Set(['book_ambulance'])) // Track which tabs have been loaded
  const [tabLoadingStates, setTabLoadingStates] = useState({})
  const [trackingBookingId, setTrackingBookingId] = useState(null)
  const [trackingJourneyDirection, setTrackingJourneyDirection] = useState('OUTBOUND')
  const [activeBookingTab, setActiveBookingTab] = useState('book_ambulance') // For booking navbar tabs
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('Overview') // For analytics navbar tabs
  const [activePaymentTab, setActivePaymentTab] = useState('Overview') // For payment navbar tabs
  const [activeTrackingTab, setActiveTrackingTab] = useState('Booked') // For tracking navbar tabs
  const [activeHistoryTab, setActiveHistoryTab] = useState('all') // For history navbar tabs (filter)
  const [currentPage, setCurrentPage] = useState(1) // For pagination
  const [itemsPerPage, setItemsPerPage] = useState(10) // Entries per page (controlled by dropdown)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })
  // Dynamic date range for Tracking and History
  const [trackingDateFrom, setTrackingDateFrom] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  })
  const [trackingDateTo, setTrackingDateTo] = useState(() => {
    const d = new Date(); d.setHours(23, 59, 59, 999); return d;
  })
  const [historyDateFrom, setHistoryDateFrom] = useState(null)
  const [historyDateTo, setHistoryDateTo] = useState(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false) // Mobile menu state
  const [vehicleHistoryVehicleId, setVehicleHistoryVehicleId] = useState(null)
  const [vehicleHistoryPlateNumber, setVehicleHistoryPlateNumber] = useState('')

  // Handle changes to items-per-page dropdown for tracking views
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit)
    setCurrentPage(1)
    setPagination(prev => ({
      ...prev,
      page: 1,
      limit,
      totalPages: prev.total ? Math.ceil(prev.total / limit) || 1 : prev.totalPages
    }))
  }

  // Notification system
  const {
    notification,
    showSuccess,
    showWarning,
    showNotification,
    hideNotification
  } = useNotifications()

  // Track bookings and check for unassigned ambulances
  const [activeBookings, setActiveBookings] = useState([])
  const [trackingData, setTrackingData] = useState({})
  const shownToastRef = useRef(new Set()) // Track which bookings have already shown a toast

  // Custom menu items for Hospital Dashboard (Main Menu section)
  const menuItems = [
    { id: 'book_ambulance', icon: Truck, label: 'Book Ambulance' },
    { id: 'tracking', icon: MapPin, label: 'Tracking' },
    { id: 'ambulance_tracking', icon: Navigation, label: 'Ambulance Tracking' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'history', icon: History, label: 'History' },
    { id: 'partnerships', icon: Hospital, label: 'Partnerships' },
    { id: 'partnership_requests', icon: Building2, label: 'Partnership Requests' },
    { id: 'partner_fleets', icon: Mail, label: 'Invitation' },
    { id: 'all_vehicles', icon: Car, label: 'All Vehicles' },
    { id: 'settings', icon: Settings, label: 'Settings' },
    { id: 'profile', icon: User, label: 'Profile' },
    { id: 'instant_booking_duplicate', icon: Zap, label: 'Instant Booking' }
  ];

  const contacts = [
    { label: "Emergency", number: "102" },
    { label: "Hospital", number: "+91 98765-43210" },
  ]

  // Check for bookingId in URL parameters and switch to tracking view if present
  useEffect(() => {
    const bookingId = searchParams.get('bookingId')
    const urlDirection = searchParams.get('direction')
    if (bookingId && selectedSidebarItem !== 'tracking') {
      setSelectedSidebarItem('tracking')
      setTrackingBookingId(bookingId)
      if (urlDirection) setTrackingJourneyDirection(urlDirection.toUpperCase())
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('bookingId')
      newSearchParams.delete('direction')
      setSearchParams(newSearchParams, { replace: true })
    }
    // eslint-disable-next-line
  }, []) // Only run once on mount to check initial URL params

  // Memoized sidebar item change handler with data loading
  const handleSidebarItemChange = useCallback(async (itemId) => {
    setSelectedSidebarItem(itemId)
    setIsMobileMenuOpen(false) // Close mobile sidebar on item click

    // Show notification for navigation
    const sectionNames = {
      'book_ambulance': 'Book Ambulance',
      'tracking': 'Tracking',
      'ambulance_tracking': 'Ambulance Tracking',
      'history': 'History',
      'partnerships': 'Partnerships',
      'partner_fleets': 'Invitation',
      // 'operators': 'Operators',
      // 'drivers': 'Drivers',
      'all_vehicles': 'All Vehicles',
      'analytics': 'Analytics',
      'payments': 'Payments',
      'settlements': 'Settlements',
      'settings': 'Settings',
      'profile': 'Profile',
      'instant_booking_duplicate': 'Instant Booking'
    }

    showSuccess('Navigation', `Switched to ${sectionNames[itemId] || itemId} section`)

    // If item hasn't been loaded yet, load its data
    if (!loadedTabs.has(itemId)) {
      setTabLoadingStates(prev => ({ ...prev, [itemId]: true }))

      try {
        // Simulate API call for item-specific data
        await new Promise(resolve => setTimeout(resolve, 500))

        // Load item-specific data based on item id
        switch (itemId) {
          case 'analytics':
            // Analytics data would be loaded here
            showSuccess('Data Loaded', 'Analytics data loaded successfully')
            break
          case 'payments':
            // Payment data would be loaded here
            showSuccess('Data Loaded', 'Payment data loaded successfully')
            break
          case 'settlements':
            // Settlement data would be loaded here
            showSuccess('Data Loaded', 'Settlement data loaded successfully')
            break
          default:
            break
        }

        setLoadedTabs(prev => new Set([...prev, itemId]))
      } catch (error) {
        // Handle error silently or show user-friendly message
        console.error('Failed to load data:', error)
      } finally {
        setTabLoadingStates(prev => ({ ...prev, [itemId]: false }))
      }
    }
  }, [loadedTabs, showSuccess])

  const handleCreateRequest = useCallback((newRequest) => {
    setRequests(prev => [
      ...prev,
      { id: `REQ-${Date.now()}`, status: "Pending", ...newRequest },
    ])
    setIsModalOpen(false)
    showSuccess('Request Created', 'New ambulance request has been created successfully')
  }, [showSuccess])

  // Monitor bookings for unassigned ambulances when on tracking page
  useEffect(() => {
    if (selectedSidebarItem !== 'tracking') {
      return
    }

    const fetchBookingsAndCheckAssignments = async () => {
      try {
        // Fetch active bookings
        const bookingsResponse = await hospitalService.getActiveBookings()

        if (!bookingsResponse.success || !bookingsResponse.data) {
          return
        }

        const bookings = Array.isArray(bookingsResponse.data)
          ? bookingsResponse.data
          : []

        setActiveBookings(bookings)

        // Fetch tracking data for each booking and check for unassigned ambulances
        const newTrackingData = {}
        const trackingPromises = bookings.map(async (booking) => {
          try {
            const bookingId = booking.booking_id || booking.id
            if (!bookingId) return

            const trackingResponse = await hospitalService.getBookingTracking(bookingId)

            if (trackingResponse.success && trackingResponse.data) {
              newTrackingData[bookingId] = trackingResponse.data

              // Check if ambulance is not assigned
              // Criteria: Check booking object for vehicle/ambulance fields, and tracking for driverLocation
              const tracking = trackingResponse.data
              const bookingHasAmbulance = booking.vehicle ||
                booking.ambulance_id ||
                booking.ambulance_number ||
                booking.vehicle_id

              // Check tracking data - driverLocation being null indicates no ambulance assigned
              const trackingHasLocation = tracking?.tracking?.driverLocation !== null &&
                tracking?.tracking?.driverLocation !== undefined

              // Booking is unassigned if both booking and tracking show no ambulance
              const isUnassigned = !bookingHasAmbulance && !trackingHasLocation

              // Show toast if ambulance is not assigned and we haven't shown it for this booking yet
              if (isUnassigned && !shownToastRef.current.has(bookingId)) {
                shownToastRef.current.add(bookingId)

                // Show warning notification with explicit auto-close settings
                showNotification({
                  type: 'warning',
                  title: 'Ambulance Not Assigned',
                  message: `Booking #${bookingId} does not have an ambulance assigned. Please assign an ambulance to proceed.`,
                  autoClose: true,
                  autoCloseDelay: 5000
                })

                // Fallback: Ensure notification closes after 5 seconds even if auto-close fails
                setTimeout(() => {
                  hideNotification()
                }, 5000)

                // Remove from shown set after 10 seconds to allow re-notification if still unassigned
                setTimeout(() => {
                  shownToastRef.current.delete(bookingId)
                }, 10000)
              }
            }
          } catch (trackingError) {
            // Silently fail for individual tracking errors
            console.warn(`Could not fetch tracking for booking:`, trackingError)
          }
        })

        await Promise.all(trackingPromises)
        setTrackingData(newTrackingData)
      } catch (err) {
        console.error('Error fetching bookings:', err)
      }
    }

    // Initial fetch
    fetchBookingsAndCheckAssignments()

    // Set up interval to check every 30 seconds
    const intervalId = setInterval(fetchBookingsAndCheckAssignments, 5000)

    return () => {
      clearInterval(intervalId)
      // Clear shown toasts when leaving tracking page
      shownToastRef.current.clear()
      // Hide any active notification when leaving tracking page
      hideNotification()
    }
  }, [selectedSidebarItem, showNotification, hideNotification])

  const renderContent = () => {
    // Loading component for active section
    if (tabLoadingStates[selectedSidebarItem]) {
      return (
        <div className="flex items-center justify-center py-6 sm:py-8">
          <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
          <span className="text-gray-600 text-sm sm:text-base">Loading {selectedSidebarItem} data...</span>
        </div>
      )
    }

    switch (selectedSidebarItem) {
      case 'book_ambulance':
        return (
          <div className="relative space-y-4 sm:space-y-6 pb-24">
            {/* Booking Content based on active tab */}
            {activeBookingTab === 'book_ambulance' && (
              <div className="space-y-4">
                <Suspense fallback={
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                    <span className="text-gray-600 text-sm sm:text-base">Loading hospital booking...</span>
                  </div>
                }>
                  <HospitalBooking />
                </Suspense>
              </div>
            )}

            {/* Floating Booking Navbar - Fixed at bottom center */}
            {/* COMMENTED OUT: Instant Booking navbar disabled for Hospital Dashboard */}
            {/* <FloatingNavBar 
              tabs={['Instant Booking']}
              activeTab="Instant Booking"
              onTabChange={() => {
                setActiveBookingTab('book_ambulance');
              }}
            /> */}
          </div>
        )

      case 'tracking':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            {/* Top Bar: Date filter card + Pagination */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2 sm:mb-4">
              <div className="bg-white rounded-xl shadow p-4 flex-1 sm:flex-initial sm:max-w-md">
                <DateRangeSelector
                  fromDate={trackingDateFrom}
                  toDate={trackingDateTo}
                  onRangeChange={(fromDate, toDate) => {
                    setTrackingDateFrom(fromDate || null);
                    setTrackingDateTo(toDate || null);
                    setCurrentPage(1);
                  }}
                  showLabels={true}
                  fromLabel="From"
                  toLabel="To"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages || 1}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                  />
                  <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Page {currentPage} of {pagination.totalPages || 1}</span>
                </div>

                <div className="w-full sm:w-auto">
                  <TotalPageNumber
                    totalPages={pagination.totalPages || 1}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </div>

            {/* SAdminDashboard Content */}
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading tracking...</span>
              </div>
            }>
              <SAdminDashboard showHeader={false} hideMetricsCards={true} currentPage={currentPage} itemsPerPage={itemsPerPage} onPaginationChange={setPagination} dateFrom={trackingDateFrom} dateTo={trackingDateTo} />
            </Suspense>

            {/* Floating Bottom Navbar */}
            <FloatingNavBar
              tabs={['Booked', 'Scheduled', 'On Duty', 'Off Duty', 'Map']}
              activeTab={activeTrackingTab}
              onTabChange={(tab) => {
                console.log('Tracking navbar tab clicked:', tab);
                setActiveTrackingTab(tab);
              }}
            />
          </div>
        )

      case 'ambulance_tracking':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2 sm:mb-4">
              <div className="bg-white rounded-xl shadow p-4 flex-1 sm:flex-initial sm:max-w-md">
                <DateRangeSelector
                  fromDate={trackingDateFrom}
                  toDate={trackingDateTo}
                  onRangeChange={(fromDate, toDate) => {
                    setTrackingDateFrom(fromDate || null);
                    setTrackingDateTo(toDate || null);
                    setCurrentPage(1);
                  }}
                  showLabels={true}
                  fromLabel="From"
                  toLabel="To"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages || 1}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                  />
                  <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Page {currentPage} of {pagination.totalPages || 1}</span>
                </div>

                <div className="w-full sm:w-auto">
                  <TotalPageNumber
                    totalPages={pagination.totalPages || 1}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </div>

            {/* Ambulance Tracking Content */}
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading ambulance tracking...</span>
              </div>
            }>
              <HospitalTrackingScreen
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onPaginationChange={setPagination}
                dateFrom={trackingDateFrom}
                dateTo={trackingDateTo}
              />
            </Suspense>

            {/* Floating Bottom Navbar */}
            <FloatingNavBar
              tabs={['Active', 'Assigned', 'En Route', 'Completed', 'Map']}
              activeTab={activeTrackingTab}
              onTabChange={(tab) => {
                console.log('Ambulance tracking navbar tab clicked:', tab);
                setActiveTrackingTab(tab);
              }}
            />
          </div>
        )

      case 'history':
        return (
          <div className="space-y-3 sm:space-y-4 pb-20">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2 sm:mb-4">
              <div className="bg-white rounded-xl shadow p-4 flex-1 sm:flex-initial sm:max-w-md">
                <DateRangeSelector
                  fromDate={historyDateFrom}
                  toDate={historyDateTo}
                  onRangeChange={(fromDate, toDate) => {
                    setHistoryDateFrom(fromDate || null);
                    setHistoryDateTo(toDate || null);
                    setCurrentPage(1);
                  }}
                  showLabels={true}
                  fromLabel="From"
                  toLabel="To"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages || 1}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                  />
                  <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Page {currentPage} of {pagination.totalPages || 1}</span>
                </div>

                <div className="w-full sm:w-auto">
                  <TotalPageNumber
                    totalPages={pagination.totalPages || 1}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={() => { }}
                  />
                </div>
              </div>
            </div>

            {/* Main Content */}
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading history...</span>
              </div>
            }>
              <HistoryScreen statusFilter={activeHistoryTab} currentPage={currentPage} onPageChange={setCurrentPage} onPaginationChange={setPagination} dateFrom={historyDateFrom} dateTo={historyDateTo} />
            </Suspense>

            {/* Floating Bottom Navbar */}
            <FloatingNavBar
              tabs={['All', 'Completed', 'Confirmed', 'Cancelled', 'Assigned', 'Arrived', 'In Progress', 'Pending']}
              activeTab={activeHistoryTab}
              onTabChange={(tab) => {
                console.log('History filter tab clicked:', tab);
                setActiveHistoryTab(tab);
              }}
            />
          </div>
        )

      // case 'operators':
      //   return (
      //     <div className="space-y-3 sm:space-y-4">
      //       <Suspense fallback={
      //         <div className="flex items-center justify-center py-6 sm:py-8">
      //           <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
      //           <span className="text-gray-600 text-sm sm:text-base">Loading operators...</span>
      //         </div>
      //       }>
      //         <OperatorScreen />
      //       </Suspense>
      //     </div>
      //   )

      // case 'drivers':
      //   return (
      //     <div className="space-y-3 sm:space-y-4">
      //       <Suspense fallback={
      //         <div className="flex items-center justify-center py-6 sm:py-8">
      //           <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
      //           <span className="text-gray-600 text-sm sm:text-base">Loading drivers...</span>
      //         </div>
      //       }>
      //         <DriverScreen />
      //       </Suspense>
      //     </div>
      //   )

      case 'analytics':
        return (
          <div className="relative space-y-3 sm:space-y-4 pb-24">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading analytics...</span>
              </div>
            }>
              <HospitalAnalytics activeTab={activeAnalyticsTab} />
            </Suspense>

            {/* Floating Analytics Navbar - Fixed at bottom center */}
            <FloatingNavBar
              tabs={['Overview', 'Performance', 'Tracking', 'Utilization', 'Financial']}
              activeTab={activeAnalyticsTab}
              onTabChange={setActiveAnalyticsTab}
            />
          </div>
        )

      case 'payments':
        return (
          <div className="relative space-y-3 sm:space-y-4 pb-24">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading payments...</span>
              </div>
            }>
              <PaymentReports activeTab={activePaymentTab} />
            </Suspense>

            {/* Floating Payment Navbar - Fixed at bottom center */}
            <FloatingNavBar
              tabs={['Overview', 'Transactions', 'QR Analytics', 'Reports']}
              activeTab={activePaymentTab}
              onTabChange={setActivePaymentTab}
            />
          </div>
        )

      case 'settlements':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading settlements...</span>
              </div>
            }>
              <Settlements />
            </Suspense>
          </div>
        )

      case 'settings':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading settings...</span>
              </div>
            }>
              <SettingsScreen />
            </Suspense>
          </div>
        )

      case 'partner_fleets':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading invitation...</span>
              </div>
            }>
              <PartnerFleetManagement />
            </Suspense>
          </div>
        )

      case 'partnerships':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading partnerships...</span>
              </div>
            }>
              <Partnerships />
            </Suspense>
          </div>
        )

      case 'partnership_requests':
        return (
          <div className="space-y-3 sm:space-y-4">
            <HospitalPartnershipRequests />
          </div>
        )

      case 'all_vehicles':
        if (vehicleHistoryVehicleId) {
          return (
            <div className="space-y-3 sm:space-y-4">
              <VehicleHistoryPage
                vehicleId={vehicleHistoryVehicleId}
                plateNumber={vehicleHistoryPlateNumber}
                onBack={() => {
                  setVehicleHistoryVehicleId(null)
                  setVehicleHistoryPlateNumber('')
                }}
              />
            </div>
          )
        }
        return (
          <div className="space-y-3 sm:space-y-4">
            <VehiclesPage
              hideAddVehicle={true}
              hideDateFilter={true}
              onViewHistory={(vehicleId, plateNumber) => {
                setVehicleHistoryVehicleId(vehicleId)
                setVehicleHistoryPlateNumber(plateNumber || '')
              }}
            />
          </div>
        )

      case 'profile':
        return (
          <div className="space-y-3 sm:space-y-4">
            <ProfileScreen
              onEditProfile={() => {
                showSuccess('Profile', 'Edit profile functionality would be implemented here')
              }}
              onWalletClick={() => {
                showSuccess('Wallet', 'Wallet functionality would be implemented here')
              }}
              onFeedbackClick={() => {
                showSuccess('Feedback', 'Feedback & Support functionality would be implemented here')
              }}
              onPasswordChangeClick={() => {
                // Change Password UI is handled directly in ProfileScreen component
                console.log('Change Password clicked');
              }}
              onTermsClick={() => {
                showSuccess('Terms', 'Terms & Conditions functionality would be implemented here')
              }}
              onAboutClick={() => {
                showSuccess('About', 'About Us functionality would be implemented here')
              }}
              onNotificationClick={() => {
                showSuccess('Notifications', 'Notifications functionality would be implemented here')
              }}
            />
          </div>
        )

      case 'instant_booking_duplicate':
        return (
          <div className="h-full w-full">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading instant booking...</span>
              </div>
            }>
              <AmbulanceInstantBookingDuplicate
                onTrackBookingClick={(bookingId) => {
                  console.log('📍 [HospitalDashboard] Track Booking clicked from success modal, bookingId:', bookingId);
                  // Set the booking ID for tracking
                  setTrackingBookingId(bookingId);
                  // Set URL param to trigger existing tracking flow
                  setSearchParams({ bookingId });
                  // Switch to tracking view
                  setSelectedSidebarItem('tracking');
                  // Update the active tracking tab to show the booking
                  setActiveTrackingTab('Booked');
                  // Show notification
                  showSuccess('Navigation', `Switched to tracking view for booking ${bookingId}`);
                }}
              />
            </Suspense>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="flex h-screen" style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
    }}>
      {/* Sidebar - always visible */}
      <Sidebar
        menuItems={menuItems}
        selectedItem={selectedSidebarItem}
        onItemClick={setSelectedSidebarItem}
        defaultSelectedItem="book_ambulance"
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <MaintenanceBanner />
        {/* Conditionally render topbar - hide it for duplicate booking */}
        {selectedSidebarItem !== 'instant_booking_duplicate' && (
          <div className="px-2 md:px-3 lg:px-3 relative z-30">
            <UniversalTopBar
              title="Hospital Dashboard"
              subtitle="Manage hospital operations, requests, and analytics."
              userRole="hospital"
              onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              isMobileMenuOpen={isMobileMenuOpen}
            />
          </div>
        )}

        <main className={`flex-1 overflow-y-auto ${selectedSidebarItem !== 'instant_booking_duplicate' ? 'px-2 md:px-3 lg:px-3 pt-2.5 pb-4' : 'p-2'}`}>
          {/* Dynamic Content based on sidebar selection */}
          {renderContent()}
        </main>
      </div>


      {/* Notification Container */}
      <NotificationContainer notification={notification} />
    </div>
  )
}

export default HospitalDashboard